import React from "react";
import { DateTime } from "luxon";
import {
  Text,
  Flex,
  Card,
  Link,
  Table,
  TableCell,
  TableRow,
  TableBody,
  Banner,
} from "@jsq/ds/core";
import { DistributionBatchRowItem } from "../../DistributionBatchesTable/types";
import Money from "../../Money";
import {
  transactionDetailsCard,
  cardTitle,
  tableCell,
  tableRow,
  remainingToPayBanner,
} from "./styles_vanilla.css";

interface TransactionDetailsCardProps {
  distribution: DistributionBatchRowItem;
  entityUrl?: string;
  /**
   * Callback fired when the "Create Payment" button is clicked.
   * Button is only shown when this callback is provided and remainingToPayAmount > 0.
   */
  onCreatePayment?: () => void;
}

export const TransactionDetailsCard: React.FC<TransactionDetailsCardProps> = ({
  distribution,
  entityUrl,
  onCreatePayment,
}) => {
  const formattedDate = distribution.distributionDate.toLocaleString(DateTime.DATE_MED);
  const hasRemainingToPay = distribution.remainingToPayAmount.amount > 0;
  const showCreatePaymentButton = onCreatePayment && hasRemainingToPay;

  return (
    <Card className={transactionDetailsCard}>
      <Flex flexDirection="column">
        <Text className={cardTitle} variant="secondary-md">
          TRANSACTION DETAILS
        </Text>
        <Table condensed>
          <TableBody>
            <TableRow className={tableRow}>
              <TableCell className={tableCell}>
                <Text variant="secondary-md">Client name</Text>
              </TableCell>
              <TableCell>
                <Text variant="regular-md">{distribution.clientName}</Text>
              </TableCell>
            </TableRow>
            <TableRow className={tableRow}>
              <TableCell className={tableCell}>
                <Text variant="secondary-md">Investment entity</Text>
              </TableCell>
              <TableCell>
                {entityUrl ? (
                  <Link
                    href={entityUrl}
                    align="left"
                    icon="external-link"
                    iconPosition="right"
                    shouldOpenInNewTab
                  >
                    {distribution.entityName}
                  </Link>
                ) : (
                  <Text variant="regular-md">{distribution.entityName}</Text>
                )}
              </TableCell>
            </TableRow>
            <TableRow className={tableRow}>
              <TableCell className={tableCell}>
                <Text variant="secondary-md">Amount</Text>
              </TableCell>
              <TableCell>
                <Text variant="regular-md">
                  <Money money={distribution.distributionAmount} rounded={false} />
                </Text>
              </TableCell>
            </TableRow>
            <TableRow className={tableRow}>
              <TableCell className={tableCell}>
                <Text variant="secondary-md">Distribution date</Text>
              </TableCell>
              <TableCell>
                <Text variant="regular-md">{formattedDate}</Text>
              </TableCell>
            </TableRow>
            <TableRow className={tableRow}>
              <TableCell className={tableCell}>
                <Text variant="secondary-md">Positions</Text>
              </TableCell>
              <TableCell>
                <Text variant="regular-md">{distribution.positionsCount}</Text>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {showCreatePaymentButton && (
          <Banner
            className={remainingToPayBanner}
            variant="primary"
            data-testid="remaining-to-pay-banner"
            title=""
            description={
              <>
                There is <Money money={distribution.remainingToPayAmount} rounded={false} />{" "}
                remaining to pay
              </>
            }
            isDescriptionJsx
            buttonProps={{
              children: "Create payment",
              variant: "primary",
              onClick: onCreatePayment,
              "data-testid": "create-payment-button",
            }}
          />
        )}
      </Flex>
    </Card>
  );
};
